/**
 * Laravel Echo Configuration
 *
 * Shared configuration for Laravel Echo/Reverb connections
 * Used by both web and mobile applications
 */

// Define Pusher types inline to avoid dependency issues
interface PusherOptions {
  cluster?: string;
  encrypted?: boolean;
  auth?: {
    headers?: Record<string, string>;
    params?: Record<string, string>;
  };
  authEndpoint?: string;
  authTransport?: 'ajax' | 'jsonp';
  activityTimeout?: number;
  pongTimeout?: number;
  unavailableTimeout?: number;
  [key: string]: unknown;
}

/**
 * Laravel Echo configuration interface
 */
export interface EchoConfig {
  /** Reverb/Pusher host (e.g., 'localhost', '127.0.0.1', 'reverb.example.com') */
  host: string;
  /** Reverb/Pusher port */
  port: number;
  /** Application key from Laravel broadcasting config */
  key: string;
  /** Use TLS/secure connection (wss instead of ws) */
  forceTLS: boolean;
  /** Optional: auth endpoint for private/presence channels */
  authEndpoint?: string;
  /** Optional: additional Pusher configuration */
  pusherConfig?: PusherOptions;
  /** Cluster */
  cluster?: string;
}

/**
 * Environment-specific Echo configuration options
 */
export interface EchoEnvironmentConfig {
  /** Reverb host */
  host?: string;
  /** Reverb port */
  port?: string | number;
  /** Application key */
  key?: string;
  /** Connection scheme (http/https) */
  scheme?: string;
  /** Auth endpoint */
  authEndpoint?: string;
  /** Cluster */
  cluster?: string;
}

/**
 * Create Echo configuration from environment variables
 *
 * @param env - Environment configuration object
 * @param defaults - Optional default values
 * @returns Complete EchoConfig
 *
 * @example
 * ```typescript
 * // Web (Vite)
 * const config = createEchoConfig({
 *   host: getEnv('VITE_REVERB_HOST'),
 *   port: getEnv('VITE_REVERB_PORT'),
 *   key: getEnv('VITE_REVERB_APP_KEY'),
 *   scheme: getEnv('VITE_REVERB_SCHEME'),
 * });
 *
 * // Mobile (React Native with react-native-config)
 * const config = createEchoConfig({
 *   host: Config.REVERB_HOST,
 *   port: Config.REVERB_PORT,
 *   key: Config.REVERB_APP_KEY,
 *   scheme: Config.REVERB_SCHEME,
 * });
 * ```
 */
export function createEchoConfig(
  env: EchoEnvironmentConfig,
  defaults?: Partial<EchoConfig>
): EchoConfig {
  const port = typeof env.port === 'string' ? parseInt(env.port, 10) : env.port;
  const scheme = env.scheme?.toLowerCase();
  const useTLS = scheme === 'https' || scheme === 'wss';

  const result: EchoConfig = {
    host: env.host || defaults?.host || 'localhost',
    port: port || defaults?.port || 8080,
    key: env.key || defaults?.key || 'app-key',
    forceTLS: useTLS,
    authEndpoint:
      env.authEndpoint || defaults?.authEndpoint || '/api/broadcasting/auth',
    cluster: env.cluster || defaults?.cluster || 'us2', // reverb does not need a cluster so we can use any cluster
  };
  if (defaults?.pusherConfig !== undefined) {
    result.pusherConfig = defaults.pusherConfig;
  }
  return result;
}

/**
 * Default Echo configuration for development
 */
export const DEFAULT_ECHO_CONFIG: EchoConfig = {
  host: 'localhost',
  port: 8080,
  key: 'app-key',
  forceTLS: false,
  authEndpoint: '/api/broadcasting/auth',
};

/**
 * Validate Echo configuration
 *
 * @param config - Echo configuration to validate
 * @throws Error if configuration is invalid
 */
export function validateEchoConfig(config: EchoConfig): void {
  if (!config.host) {
    throw new Error('[Echo Config] host is required');
  }

  if (!config.port || config.port <= 0 || config.port > 65535) {
    throw new Error('[Echo Config] port must be a valid port number (1-65535)');
  }

  if (!config.key) {
    throw new Error('[Echo Config] key is required');
  }

  if (typeof config.forceTLS !== 'boolean') {
    throw new Error('[Echo Config] forceTLS must be a boolean');
  }
}

/**
 * Check if Echo configuration is valid
 *
 * @param config - Echo configuration to check
 * @returns true if valid, false otherwise
 */
export function isValidEchoConfig(
  config: Partial<EchoConfig>
): config is EchoConfig {
  try {
    validateEchoConfig(config as EchoConfig);
    return true;
  } catch {
    return false;
  }
}
